<template>
  <div class="gmf-token-shop" :style="tokenShopStyle">
    <header class="gmf-token-shop-header" @touchmove.prevent>
      <img :src="coinIcon" class="gmf-token gmf-token-lg" />
      <h3 class="gmf-subheader gmf-mb-0 gmf-ml-2" id="gmf-token-shop-title">{{ t('title') }}</h3>
    </header>
    <div v-if="loadError" class="gmf-token-shop-error">
      {{ t('failed_to_load') }}
    </div>
    <template v-else>
      <div class="gmf-token-balances" @touchmove.prevent id="gmf-token-shop-description">
        <span>
          {{ t('description') }}
        </span>
        <template v-if="userBalances.length > 0">
          <span
            v-for="balance, index in userBalances"
            :key="balance.token.id" 
            class="gmf-token-balance"
          >
            &nbsp;<img class="gmf-token" :src="balance.token.icon" /> <span class="gmf-token-amount">{{ formatVirtualCurrency(balance.amount) }}</span> {{ balance.token.shortName }}
            <span v-if="index < userBalances.length - 1">,</span>
          </span>
        </template>
        <span v-else class="gmf-token-balance">
          &nbsp;<img :src="coinIcon" class="gmf-token gmf-token-balance-icon" /> <b>0</b> {{ t('coins') }}
        </span>
      </div>
      <LoadingSpinner v-if="isLoading" class="token-shop-loading-spinner" />
      <template v-else>
        <HorizontalPills
          ref="horizontalPills"
          :items="categories"
          class="gmf-mt-4 gmf-token-shop-menu"
          :selectedItem="selectedCategory"
          @update:selectedItem="selectCategory"
        >
          <template v-slot="{ item }">
            <img v-if="item.iconUrl" :src="item.iconUrl" class="gmf-token-shop-menu-icon" />
          {{ getCategoryTranslations(item) }}
          </template>
        </HorizontalPills>
        <div v-if="selectedCategory" ref="itemGrid" class="gmf-scrollable gmf-token-shop-content" @touchstart="handleTouchStart" @touchmove="handleTouchMove" :key="selectedCategory.id">
          <div
            v-if="selectedCategory.id === 'start'"
            role="tabpanel"
            :aria-labelledby="`gmf-tab-${selectedCategory.id}`"
          >
            <img v-if="bannerImage" :src="bannerImage" class="gmf-token-shop-banner-image" />
            <template v-if="highlightBundles.length > 0">
              <div class="gmf-subheader gmf-mt-4">
                {{ t('highlight.bundles') }}
              </div>
              <ItemGrid :items="highlightBundles" :category="selectedCategory" :columns="2" textAlign="left" />
            </template>
            <!-- <div class="gmf-subheader">
              Märken
            </div>
            <div v-if="brandCategories.length > 0" class="gmf-token-shop-category-grid">
              <CategoryCard v-for="category in brandCategories" :key="category.id" :category="category" @click="selectedCategory = category" />
            </div> -->
            <div class="gmf-subheader gmf-mt-4">
              {{ t('highlight.parts') }}
            </div>
            <ItemGrid :items="highlightItems" :category="selectedCategory" :showNames="false" />
          </div>
          <ItemGrid
            v-else
            :items="items"
            :category="selectedCategory"
            :showNames="false"
            role="tabpanel"
            :aria-labelledby="`gmf-tab-${selectedCategory.id}`"
          />
        </div>
      </template>
    </template>
  </div>
  <div
    v-if="checkoutIsOpen"
    class="gmf-modal-overlay gmf-modal-overlay-absolute gmf-token-shop-checkout-bottom"
    @click.stop="closeCheckout"
    @touchmove.stop="closeCheckout"
    role="dialog"
    aria-modal="true"
    aria-labelledby="gmf-item-details-name"
    aria-describedby="gmf-item-details-description"
  >
    <ItemDetails
      ref="itemDetails"
      :item="selectedItem"
      :showPrice="true"
      @close="closeCheckout"
      @click.prevent.stop
      @touchmove.prevent.stop
      :showCloseButton="true"
      @success="onItemPurchase"
      @error="onItemPurchaseError"
      @selectColor="selectedColor = $event"
    />
  </div>
  <Modal v-model="showSuccessPage" :stackModals="true" class="gmf-token-shop-rewards">
    <ItemRewards
      :items="rewardItems"
      @close="showSuccessPage = false"
      :title="t('success.title')"
      show3dAvatarStep
      :description="t('success.description')"
    />
  </Modal>
</template>
<style>
.gmf-popup-token-shop {
  height: 100%;
  max-height: 1000px;
  padding: 0;
}

.gmf-token-shop {
  position: relative;
  border-radius: var(--gmf-modal-rounding);
  height: 100%;
  overflow: hidden;
  max-width: 100%;
  width: 600px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: var(--gmf-p-4);
  background-color: var(--gmf-col-lightgray);
}

.token-shop-loading-spinner {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  margin: 0 auto;
}

.gmf-token-shop-rewards {
  width: 450px;
  padding: 0;
  max-width: 80vw;
  overflow: hidden;
}

.gmf-horizontal-pills-container {
  flex-shrink: 0;
}

.gmf-token-shop-header {
  display: flex;
  align-items: center;
  gap: var(--gmf-p-2);
  margin-bottom: var(--gmf-p-3);
  padding: 0 var(--gmf-p-4);
}

.gmf-token-shop-error {
  padding: var(--gmf-p-4);
}

.gmf-token-balances {
  padding: 0 var(--gmf-p-4);
}

.gmf-token-balance {
  display: inline;
}

.gmf-token-balance-icon {
  display: inline;
}

.gmf-token-amount {
  color: var(--gmf-col-black);
  font-weight: bold;
}

.gmf-token-balance > img {
  vertical-align: middle;
  display: inline;
  margin-top: -2px;
}

.gmf-token-shop-content {
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 0 var(--gmf-p-4);
  height: 100%;
}

.gmf-token-shop-content::-webkit-scrollbar {
  display: none;
}

.gmf-modal-overlay-absolute {
  position: absolute;
  overflow: hidden;
  border-radius: var(--gmf-modal-rounding);
}

.gmf-item-details-container {
  position: absolute;
  width: 100%;
  bottom: 0;
}

.gmf-token-shop-content .gmf-item-grid {
  margin-top: var(--gmf-p-4);
}

.gmf-token-shop-banner-image {
  width: calc(100% + var(--gmf-p-4) * 2);
  margin: var(--gmf-p-4) calc(0px - var(--gmf-p-4));
  max-height: 200px;
  object-fit: cover;
  margin-bottom: 0;
  max-width: unset;
}

.gmf-token-shop-menu {
  background-color: var(--gmf-col-white);
}

.gmf-token-shop-menu-icon {
  /* width: 24px; */
  height: 24px;
  margin-inline: var(--gmf-p-2);
}

.gmf-token-shop-category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: var(--gmf-p-4) var(--gmf-p-3);
  margin-bottom: var(--gmf-p-4);
  margin-top: var(--gmf-p-4);
}

.gmf-token-shop button {
  border: unset;
}
</style>
<script>
import state from '@/state';
import { computed } from 'vue';
import Modal from '../Modal.vue';
const t = state.t.prefix('token_shop');
import eventbus from '@/utils/eventbus';
const ts = state.t.prefix('avatar.slots');
import ItemDetails from '../Items/ItemDetails.vue';
import LoadingSpinner from '../LoadingSpinner.vue';
import HorizontalPills from '../HorizontalPills.vue';
import { loadTokenShop, categories } from './data.js';
import ItemGrid from '@/components/Items/ItemGrid.vue';
import { getMyInventory } from '@/utils/getMyInventory';
import { getMyTokenBalance } from '@/utils/getMyTokenBalance';
import ItemRewards from '@/components/Items/ItemRewards.vue';
import formatVirtualCurrency from '@/utils/formatVirtualCurrency';
// import CategoryCard from './CategoryCard.vue';

export default {
  components: {
    Modal,
    ItemGrid,
    ItemDetails,
    LoadingSpinner,
    ItemRewards,
    HorizontalPills,
    // CategoryCard
  },
  inject: ['modalOverlay', 'closeModal'],
  data () {
    return {
      // Token shop items
      slots: [],
      categories: [],
      selectedCategory: null,
      selectedSubCategory: null,
      checkoutIsOpen: false,
      showConfirmModal: false,
      selectedItem: null,
      selectedPrice: null, 
      selectedColor: null,
      isLoading: false,
      loadError: false,
      showSuccessPage: false,
      rewardItems: [],
      startY: 0,
      inventory: [],
    };
  },
  provide () {
    return {
      selectedCategory: computed(() => this.selectedCategory),
      inventory: computed(() => this.inventory),
    };
  },
  async created () {
    this.isLoading = true;
    const hideInventoryDetails = true;

    try {
      const [tokenBalances, inventoryIds, tokenShopRes] = await Promise.all([
        getMyTokenBalance(),
        getMyInventory(hideInventoryDetails),
        loadTokenShop(),
      ]);

      state.tokenBalances = tokenBalances;
      this.inventory = inventoryIds;
      this.isLoading = false;

      if (! tokenShopRes) {
        this.loadError = true;
        this.isLoading = false;
      } else {
        this.loadError = false;
        this.categories = categories;
        this.selectedCategory = categories[0];
      }
    } catch (err) {
      this.loadError = true;
      this.isLoading = false;
    }

    eventbus.on('checkout-item', this.checkoutItem);
    this.modalOverlay.onclick = this.overlayClickHandler.bind(this);
  },
  beforeUnmount () {
    eventbus.off('checkout-item', this.checkoutItem);
  },
  computed: {
    coinIcon () {
      return state.merchantConfig?.tokenShop.coinIcon;
    },
    bannerImage () {
      return state.merchantConfig?.tokenShop.bannerImage;
    },
    tokenShopStyle () {
      if (this.checkoutIsOpen) {
        return {
          'filter': 'blur(2px)',
        };
      }
      return {};
    },
    userBalances () {
      return state.tokenBalances || [];
    },
    items () {
      if (! this.selectedCategory) {
        return [];
      }
      if (this.selectedSubCategory) {
        return this.selectedSubCategory.items || [];
      }

      if (this.selectedCategory) {
        return this.selectedCategory.items || [];
      }

      return [];
    },
    showLargeBundles () {
      const w = this.elSize.width;
      return w < 600;
    },
    highlightBundles () {
      return this.items.filter(item => (item.type === 'item-bundle' || item.type === 'loot-crate'));
    },
    highlightItems () {
      return this.items.filter(item => item.type !== 'item-bundle' && item.type !== 'loot-crate');
    },
    brandCategories () {
      return this.categories.filter(category => category.type === 'brand');
    },
  },
  methods: {
    t,
    ts,
    formatVirtualCurrency,
    overlayClickHandler () {
      if (! this.modalOverlay.isBodyClickEvent) {
        this.checkoutIsOpen = false;
        this.$nextTick(() => {
          setTimeout(() => {
            this.closeModal();
          }, 100);
        });
      }
    },
    getCategoryTranslations (category) {
      // Add more cases here when new types of store items are added.
      if (category.id === 'start' || category.id === 'skins') {
        return t(category.id);
      }

      const slotTranslation = ts(category.id);

      return slotTranslation;
    },
    checkoutItem ({ item, price, color }) {
      this.selectedItem = item;
      this.selectedPrice = price;
      this.selectedColor = color;
      this.checkoutIsOpen = true;
    },
    closeCheckout () {
      if (! this.checkoutIsOpen) {
        return;
      }

      if (this.$refs.itemDetails && this.$refs.itemDetails.isBuying) {
        // Checkout is busy.
        return;
      }

      this.checkoutIsOpen = false;
    },
    async onItemPurchase (rewardsGiven) {
      if (rewardsGiven?.type === 'loot-crate') {
        await loadTokenShop();
        this.$nextTick(() => {
          eventbus.emit("refreshListItem", rewardsGiven.itemUuid);
        });
        this.checkoutIsOpen = false;
        return;
      }

      // Don't show the bundle reward itself in the rewards.
      const displayRewards = rewardsGiven.filter(reward => reward.type !== 'item-bundle');
      this.rewardItems = displayRewards;

      this.inventory.push(...rewardsGiven);

      this.showSuccessPage = true;
      this.checkoutIsOpen = false;

    },
    onItemPurchaseError () {
      // Reload token balances
      getMyTokenBalance().then(tokenBalances => {
        state.tokenBalances = tokenBalances;
      });

      // Reload inventory
      getMyInventory(true).then(inventoryIds => {
        this.inventory = inventoryIds;
      });
    },
    // Store the starting Y position of the touch
    handleTouchStart(e) {
      this.startY = e.touches[0].clientY;
    },

    // Prevent pull-down-to-refresh if at the top of the scroll and moving downwards
    handleTouchMove(e) {
      const currentY = e.touches[0].clientY;
      const movingDownwards = currentY > this.startY;
      const scrollTop = this.$refs.itemGrid.scrollTop;
      if(movingDownwards && scrollTop === 0) {
        e.preventDefault();
      }
    },
    selectCategory (category) {
      this.selectedCategory = category;
    },
  },
  watch: {
    selectedCategory (category) {
      if (category.categories && category.categories.length) {
        this.selectedSubCategory = category.categories[0];
      } else {
        this.selectedSubCategory = null;
      }
    },
  },
};
</script>
